import { useState, useCallback, useRef, useEffect } from 'react';
import { sendAgentMessage, onAgentResponse } from 'src/utilities/AgentChatAPIs';
import {
  AI_SEARCH_SHOW_TIMING,
  AI_SEARCH_FIRST_TOKEN_WARN_MS,
  AI_SEARCH_INTER_CHUNK_WARN_MS,
  AI_SEARCH_HARD_TIMEOUT_MS,
} from 'src/Ateam-BI/AutoMate/constants';

/**
 * Hook that manages sending messages to the ATEAM agent via AppSync
 * and subscribing to streamed response chunks.
 *
 * Chunks can arrive out of order over the WebSocket. To keep the on-screen
 * text coherent (no "Brokerage::" or "https.amazon.com" artifacts caused by
 * glued-at-the-wrong-boundary chunks), we:
 *   1. Store each chunk in a sparse array indexed by `chunkIndex`
 *   2. Walk the array from the start and concatenate only the longest
 *      contiguous prefix we have so far
 *   3. Hold back any out-of-order chunks until the gap is filled
 *
 * Timing metrics captured per response:
 *   - timeToFirstTokenMs: from mutation fire to the first STREAMING chunk
 *   - durationMs (a.k.a. time-to-last-token): from mutation fire to COMPLETE
 *
 * Returns { messages, isStreaming, waitingReason, handleChatSubmit, handleCancel }.
 *
 * messages: Array of {
 *   role: 'user' | 'assistant',
 *   content: string,
 *   durationMs?: number,
 *   timeToFirstTokenMs?: number,
 * }
 * isStreaming: true while the assistant is mid-response
 * waitingReason: null | 'first-token' | 'inter-chunk' — drives a "still
 *   waiting" banner in the ChatPanel. Cleared when a chunk arrives or the
 *   stream finishes.
 * handleCancel: tear down the current request immediately and clear the
 *   placeholder assistant message. No-op if nothing is in flight.
 */
const useAgentChat = () => {
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [isStreaming, setIsStreaming] = useState(false);
  // null | 'first-token' | 'inter-chunk' — soft-timeout signal for the UI.
  const [waitingReason, setWaitingReason] = useState(null);
  // Sparse array keyed by chunkIndex. Lets us tolerate out-of-order delivery.
  const chunksRef = useRef([]);
  // Next index we expect — the contiguous-prefix watermark.
  const nextExpectedRef = useRef(0);
  const subscriptionRef = useRef(null);
  // Monotonically-increasing ID for each handleChatSubmit call. Used to detect
  // stale async completions (e.g. a previous request's subscription resolving
  // after a newer request has already started) so the old subscription can be
  // torn down instead of leaking a WebSocket and corrupting message state.
  const requestIdRef = useRef(0);
  // Tracks whether the component is still mounted. Needed because
  // `onAgentResponse(...)` is async — if the component unmounts while the
  // subscribe() is in flight, its `.then(sub => ...)` resolves after cleanup
  // has already run. Without this ref we'd assign the sub to
  // `subscriptionRef.current` with nothing left to unsubscribe it, leaking
  // the WebSocket.
  const mountedRef = useRef(true);
  // Timers for the soft / hard timeouts. We keep these in refs so every handler
  // in this hook can cancel / reset them without re-rendering.
  const warnTimerRef = useRef(null);
  const hardTimerRef = useRef(null);
  // Timing refs — all in ms via performance.now()
  const requestStartRef = useRef(0);
  const firstTokenAtRef = useRef(null);

  const clearTimers = () => {
    if (warnTimerRef.current) { clearTimeout(warnTimerRef.current); warnTimerRef.current = null; }
    if (hardTimerRef.current) { clearTimeout(hardTimerRef.current); hardTimerRef.current = null; }
  };

  // Clean up subscription on unmount
  useEffect(() => {
    return () => {
      mountedRef.current = false;
      clearTimers();
      if (subscriptionRef.current && typeof subscriptionRef.current.unsubscribe === 'function') {
        subscriptionRef.current.unsubscribe();
      }
    };
  }, []);

  /** Join every chunk we currently have, in-order, skipping gaps. */
  const joinAllChunks = () => chunksRef.current.filter((c) => c != null).join('');

  /** Join only the contiguous prefix — stops at the first missing index. */
  const joinContiguousPrefix = () => {
    let out = '';
    const all = chunksRef.current;
    for (let i = 0; i < all.length; i++) {
      if (all[i] == null) break;
      out += all[i];
      nextExpectedRef.current = i + 1;
    }
    return out;
  };

  const resetStreamState = () => {
    chunksRef.current = [];
    nextExpectedRef.current = 0;
    firstTokenAtRef.current = null;
  };

  const handleChatSubmit = useCallback((message) => {
    // Bump the request counter first so any still-in-flight callbacks from a
    // previous call can detect they've been superseded.
    const currentRequestId = ++requestIdRef.current;

    // Tear down any previous subscription + timers before starting a new one
    clearTimers();
    if (subscriptionRef.current && typeof subscriptionRef.current.unsubscribe === 'function') {
      subscriptionRef.current.unsubscribe();
    }
    subscriptionRef.current = null;

    setMessages(prev => [...prev, { role: 'user', content: message }]);
    setMessages(prev => [...prev, { role: 'assistant', content: '' }]);
    setIsStreaming(true);
    setWaitingReason(null);
    resetStreamState();
    requestStartRef.current = performance.now();

    if (AI_SEARCH_SHOW_TIMING) {
      // eslint-disable-next-line no-console
      console.log('[useAgentChat] request started', { message });
    }

    /**
     * Soft timeout scheduler. Replaces any previous warn timer with a new one
     * pointing at the given reason. When it fires, we flip `waitingReason` so
     * the UI can show a banner, but leave the subscription open — a late
     * chunk will clear the banner and resume normal streaming.
     */
    const armWarnTimer = (reason, delay) => {
      if (warnTimerRef.current) clearTimeout(warnTimerRef.current);
      warnTimerRef.current = setTimeout(() => {
        // Same staleness/mount guards we use everywhere else
        if (!mountedRef.current || currentRequestId !== requestIdRef.current) return;
        setWaitingReason(reason);
      }, delay);
    };

    /**
     * Hard-timeout backstop. If neither first-token nor streaming ever
     * resumes, after HARD_TIMEOUT_MS we give up, replace the placeholder
     * with an error, unsubscribe, and re-enable the input.
     */
    const armHardTimer = () => {
      if (hardTimerRef.current) clearTimeout(hardTimerRef.current);
      hardTimerRef.current = setTimeout(() => {
        if (!mountedRef.current || currentRequestId !== requestIdRef.current) return;
        if (subscriptionRef.current && typeof subscriptionRef.current.unsubscribe === 'function') {
          subscriptionRef.current.unsubscribe();
        }
        subscriptionRef.current = null;
        clearTimers();
        finalizeWithDuration('The agent did not respond in time. Please try again.');
        setWaitingReason(null);
        setIsStreaming(false);
      }, AI_SEARCH_HARD_TIMEOUT_MS);
    };

    // Start the first-token soft timer and the hard ceiling right away.
    armWarnTimer('first-token', AI_SEARCH_FIRST_TOKEN_WARN_MS);
    armHardTimer();

    const updateLastMessage = (content) => {
      setMessages(prev => {
        const updated = [...prev];
        updated[updated.length - 1] = {
          ...updated[updated.length - 1],
          role: 'assistant',
          content,
        };
        return updated;
      });
    };

    /** Record first-token timestamp + attach it to the assistant message. */
    const markFirstToken = () => {
      if (firstTokenAtRef.current != null) return; // already recorded
      const ttft = Math.round(performance.now() - requestStartRef.current);
      firstTokenAtRef.current = ttft;
      setMessages(prev => {
        const updated = [...prev];
        updated[updated.length - 1] = {
          ...updated[updated.length - 1],
          timeToFirstTokenMs: ttft,
        };
        return updated;
      });
      if (AI_SEARCH_SHOW_TIMING) {
        // eslint-disable-next-line no-console
        console.log(`[useAgentChat] time-to-first-token: ${ttft}ms`);
      }
    };

    const finalizeWithDuration = (contentOverride) => {
      const durationMs = Math.round(performance.now() - requestStartRef.current);
      setMessages(prev => {
        const updated = [...prev];
        const last = updated[updated.length - 1];
        updated[updated.length - 1] = {
          ...last,
          content: contentOverride ?? last.content,
          durationMs,
        };
        return updated;
      });
      if (AI_SEARCH_SHOW_TIMING) {
        // eslint-disable-next-line no-console
        console.log(
          `[useAgentChat] time-to-last-token: ${durationMs}ms` +
          (firstTokenAtRef.current != null
            ? ` (TTFT: ${firstTokenAtRef.current}ms, streaming: ${durationMs - firstTokenAtRef.current}ms)`
            : '')
        );
      }
    };

    sendAgentMessage(message, sessionId,
      (result) => {
        // Staleness guard — a newer handleChatSubmit may have fired before
        // this mutation resolved. If so, don't overwrite the new session id
        // or mess with the new request's messages buffer.
        if (currentRequestId !== requestIdRef.current) return;
        const { conversationId, sessionId: newSessionId } = result?.data?.sendAgentMessage || {};
        if (newSessionId) setSessionId(newSessionId);
        if (conversationId) {
          onAgentResponse(conversationId,
            (response) => {
              // Staleness guard — chunks from a previous request can still
              // arrive while the old WebSocket is draining (between our
              // unsubscribe() and the actual socket close). Drop them so they
              // don't corrupt the new request's message state / chunks buffer.
              if (currentRequestId !== requestIdRef.current) return;
              const chunk = response?.value?.data?.onAgentResponse;
              if (!chunk) return;

              if (chunk.status === 'STREAMING') {
                // A chunk arrived — clear any "still waiting" banner and
                // re-arm the inter-chunk timer to watch for the next one.
                setWaitingReason(null);
                armWarnTimer('inter-chunk', AI_SEARCH_INTER_CHUNK_WARN_MS);
                // `chunkIndex` may be missing on very old backends; fall back
                // to append-order so we don't silently drop chunks.
                const idx = typeof chunk.chunkIndex === 'number'
                  ? chunk.chunkIndex
                  : chunksRef.current.length;
                chunksRef.current[idx] = chunk.content ?? '';
                // First chunk with actual content marks TTFT.
                if (chunk.content) markFirstToken();
                // Only render the contiguous prefix — out-of-order chunks wait.
                const buffered = joinContiguousPrefix();
                updateLastMessage(buffered);
              } else if (chunk.status === 'COMPLETE') {
                // Flush anything we have, including chunks past a gap (if any).
                clearTimers();
                updateLastMessage(joinAllChunks());
                finalizeWithDuration();
                setWaitingReason(null);
                setIsStreaming(false);
              } else if (chunk.status === 'ERROR') {
                console.error('Agent error:', chunk.content);
                clearTimers();
                finalizeWithDuration('Sorry, something went wrong. Please try again.');
                setWaitingReason(null);
                setIsStreaming(false);
              }
            },
            (error) => {
              // Staleness guard — don't flip isStreaming off on the active
              // request just because a superseded subscription errored.
              if (currentRequestId !== requestIdRef.current) return;
              console.error('[useAgentChat] subscription error:', error);
              clearTimers();
              setWaitingReason(null);
              setIsStreaming(false);
            }
          ).then(sub => {
            // Staleness check — if the component unmounted or a newer
            // handleChatSubmit call has run since we started this subscribe(),
            // this sub belongs to an abandoned request. Tear it down instead
            // of leaking the WebSocket.
            if (!mountedRef.current || currentRequestId !== requestIdRef.current) {
              if (sub && typeof sub.unsubscribe === 'function') sub.unsubscribe();
              return;
            }
            subscriptionRef.current = sub;
          });
        } else {
          // No conversationId — can't subscribe, clear streaming state
          clearTimers();
          setWaitingReason(null);
          setIsStreaming(false);
          setMessages(prev => prev.slice(0, -1));
        }
      },
      (error) => {
        // Staleness guard — an old mutation failing shouldn't tear down the
        // newer request's assistant placeholder or streaming state.
        if (currentRequestId !== requestIdRef.current) return;
        console.error('[useAgentChat] sendAgentMessage error:', error);
        clearTimers();
        setWaitingReason(null);
        setIsStreaming(false);
        setMessages(prev => prev.slice(0, -1));
      }
    );
  }, [sessionId]);

  /**
   * User-invoked cancel. Tear down the current request immediately, drop the
   * empty assistant placeholder if we never got any content, and re-enable
   * the input.
   */
  const handleCancel = useCallback(() => {
    // Bumping the request id means any late callbacks from the old sub will
    // fall through the staleness guards and do nothing.
    requestIdRef.current += 1;
    clearTimers();
    if (subscriptionRef.current && typeof subscriptionRef.current.unsubscribe === 'function') {
      subscriptionRef.current.unsubscribe();
    }
    subscriptionRef.current = null;
    setWaitingReason(null);
    setIsStreaming(false);
    // If the assistant placeholder never received any content, drop it so we
    // don't leave an empty bubble hanging. Otherwise keep the partial text.
    setMessages(prev => {
      if (prev.length === 0) return prev;
      const last = prev[prev.length - 1];
      if (last.role === 'assistant' && !last.content) return prev.slice(0, -1);
      return prev;
    });
  }, []);

  return { messages, isStreaming, waitingReason, handleChatSubmit, handleCancel };
};

export default useAgentChat;
